// Date utility functions for WIB (Western Indonesian Time) conversion
// WIB is UTC+7 - System is already in Asia/Jakarta timezone

/**
 * Convert a date to WIB timezone (proper handling for Asia/Jakarta system)
 * @param {string|Date} dateString - The date to convert
 * @returns {Date} - Date object in WIB timezone
 */
export const toWIB = (dateString) => {
  if (!dateString) return null;
  // Since system is already in Asia/Jakarta, just create a proper Date object
  return new Date(dateString);
};

/**
 * Format date to WIB with date and time
 * @param {string|Date} dateString - The date to format
 * @returns {object} - Object with formatted date and time strings
 */
export const formatWIBDateTime = (dateString) => {
  if (!dateString) return { dateStr: 'N/A', timeStr: 'N/A' };
  
  const date = new Date(dateString);
  
  const dateStr = date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'Asia/Jakarta'
  });
  
  const timeStr = date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Asia/Jakarta'
  });
  
  return { dateStr, timeStr: `${timeStr} WIB` };
};

/**
 * Format date to WIB date only
 * @param {string|Date} dateString - The date to format
 * @returns {string} - Formatted date string
 */
export const formatWIBDate = (dateString) => {
  if (!dateString) return 'N/A';
  
  const date = new Date(dateString);
  
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'Asia/Jakarta'
  });
};

/**
 * Format date to WIB time only
 * @param {string|Date} dateString - The date to format
 * @returns {string} - Formatted time string with WIB
 */
export const formatWIBTime = (dateString) => {
  if (!dateString) return 'N/A';
  
  const date = new Date(dateString);
  
  const timeStr = date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Asia/Jakarta'
  });
  
  return `${timeStr} WIB`;
};

/**
 * Format date for datetime-local input (in WIB)
 * @param {string|Date} dateString - The date to format
 * @returns {string} - Formatted string for datetime-local input
 */
export const formatWIBForInput = (dateString) => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  
  // Get the date components in Asia/Jakarta timezone
  const year = date.toLocaleDateString('en-CA', { timeZone: 'Asia/Jakarta' }).split('-')[0];
  const month = date.toLocaleDateString('en-CA', { timeZone: 'Asia/Jakarta' }).split('-')[1];
  const day = date.toLocaleDateString('en-CA', { timeZone: 'Asia/Jakarta' }).split('-')[2];
  
  const time = date.toLocaleTimeString('en-GB', { 
    timeZone: 'Asia/Jakarta',
    hour12: false 
  }).substring(0, 5); // Get HH:MM format
  
  return `${year}-${month}-${day}T${time}`;
};

/**
 * Convert WIB input to Asia/Jakarta timezone for API submission
 * @param {string} wibDateString - The WIB datetime string from input (format: YYYY-MM-DDTHH:mm)
 * @returns {string} - ISO string in Asia/Jakarta timezone (RFC3339 format)
 */
export const wibToUTC = (wibDateString) => {
  if (!wibDateString) return '';
  
  // Create date object - the input is already in local time (WIB)
  // When we create a Date from a datetime-local input, it's treated as local time
  const localDate = new Date(wibDateString);
  
  // Get the timezone offset for Asia/Jakarta (UTC+7 = +420 minutes)
  // We need to format the date as if it's in Asia/Jakarta timezone
  // Create a date string in Asia/Jakarta and format it properly
  
  // Use Intl.DateTimeFormat to get components in Asia/Jakarta timezone
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Jakarta',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
  
  const parts = formatter.formatToParts(localDate);
  const year = parts.find(p => p.type === 'year').value;
  const month = parts.find(p => p.type === 'month').value;
  const day = parts.find(p => p.type === 'day').value;
  const hour = parts.find(p => p.type === 'hour').value;
  const minute = parts.find(p => p.type === 'minute').value;
  const second = parts.find(p => p.type === 'second').value;
  
  // Return RFC3339 format with Asia/Jakarta timezone (+07:00)
  return `${year}-${month}-${day}T${hour}:${minute}:${second}+07:00`;
};

/**
 * Get current WIB time
 * @returns {Date} - Current date in WIB
 */
export const getCurrentWIB = () => {
  return new Date(); // System is already in WIB
};

/**
 * Format date for activity management display
 * @param {string|Date} dateString - The date to format
 * @returns {string} - Formatted date string for activity display
 */
export const formatActivityDateTime = (dateString) => {
  if (!dateString) return 'TBD';
  
  const date = new Date(dateString);
  
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Asia/Jakarta'
  }) + ' WIB';
}; 